import React, { useState, useEffect, useRef, lazy, Suspense, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import FeedbackModal from './components/FeedbackModal';
import Sidebar from './components/Sidebar';
import GuidedTour from './components/GuidedTour';
import ReconnectBanner from './components/ReconnectBanner';
import { initAnalytics, trackPageView } from './utils/analytics';
import { isJiraReauthError } from './utils/jiraConnection';

// Send cookies with every request (for JWT httpOnly cookie)
axios.defaults.withCredentials = true;

// Lazy-loaded page components (code splitting)
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Configuration = lazy(() => import('./pages/Configuration'));
const Reports = lazy(() => import('./pages/Reports'));
const OnboardingWizard = lazy(() => import('./pages/OnboardingWizard'));
const ProjectDetail = lazy(() => import('./pages/ProjectDetail'));
const Roadmap = lazy(() => import('./pages/Roadmap'));
const BacklogCreator = lazy(() => import('./pages/BacklogCreator'));
const PortfolioDashboard = lazy(() => import('./pages/PortfolioDashboard'));
const Login = lazy(() => import('./pages/Login'));
const UserManagement = lazy(() => import('./pages/UserManagement'));
const FeedbackAdmin = lazy(() => import('./pages/FeedbackAdmin'));
const About = lazy(() => import('./pages/About'));
const Profile = lazy(() => import('./pages/Profile'));
const VerifyEmail = lazy(() => import('./pages/VerifyEmail'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const EmailVerificationPending = lazy(() => import('./pages/EmailVerificationPending'));
const SharedReport = lazy(() => import('./pages/SharedReport'));
const AuditLog = lazy(() => import('./pages/AuditLog'));
const AdminAnalytics = lazy(() => import('./pages/AdminAnalytics'));
const TenantManagement = lazy(() => import('./pages/TenantManagement'));
const QualityDashboard = lazy(() => import('./pages/QualityDashboard'));
const InsightCompare = lazy(() => import('./pages/InsightCompare'));
const RubricAdmin = lazy(() => import('./pages/RubricAdmin'));
const Demo = lazy(() => import('./pages/Demo'));
const Upgrade = lazy(() => import('./pages/Upgrade'));

// Lightweight loading fallback for Suspense boundaries
function PageLoader() {
  return (
    <div className="min-h-screen bg-[#0D1B2A] flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#00C2A8] mx-auto"></div>
        <p className="mt-3 text-sm text-[#E5C07B]">Loading...</p>
      </div>
    </div>
  );
}

function AppContent() {
  // Auth state: null = loading, false = unauthenticated, object = authenticated user
  const [user, setUser] = useState(null);
  const [onboardingComplete, setOnboardingComplete] = useState(null); // null = loading
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false); // CVA-83: mobile drawer
  // SCRUM-216: guided tour — show once after first post-setup login, then never again
  const [tourDismissed, setTourDismissed] = useState(false);
  // SCRUM-448: Jira connection needs re-auth (banner shown app-wide)
  const [jiraReauthRequired, setJiraReauthRequired] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  // Check auth session on mount
  useEffect(() => {
    axios.get('/api/auth/me')
      .then(res => {
        setUser(res.data.user);
      })
      .catch(() => {
        setUser(false);
      });

    // SCRUM-131: Initialize analytics (flush timer + page-hide listener)
    initAnalytics();
  }, []);

  // SCRUM-131: Track page views on route changes
  useEffect(() => {
    trackPageView(location.pathname);
  }, [location.pathname]);

  // 401 interceptor: redirect to /login if session expires
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      response => {
        // A healthy response means the connection recovered — clear the banner.
        if (jiraReauthRequired) setJiraReauthRequired(false);
        return response;
      },
      error => {
        const publicPaths = ['/demo', '/verify-email', '/reset-password'];
        const currentPath = window.location.pathname.replace(/\/$/, '');
        const onPublicRoute = publicPaths.includes(currentPath) || currentPath.startsWith('/shared/');
        if (error.response?.status === 401 && !error.config.url?.includes('/api/auth/') && !onPublicRoute) {
          setUser(false);
          navigate('/login');
        }
        if (isJiraReauthError(error)) {
          setJiraReauthRequired(true);
        }
        return Promise.reject(error);
      }
    );
    return () => axios.interceptors.response.eject(interceptor);
  }, [navigate, jiraReauthRequired]);

  // Memoised onboarding check: only run when authenticated
  // Viewers skip onboarding entirely — they have no integrations to configure
  const checkOnboarding = useCallback(async () => {
    if (!user) return;
    if (user.role === 'viewer' || user.role === 'superadmin') {
      setOnboardingComplete(true);
      return;
    }
    try {
      const response = await axios.get('/api/onboarding/status');
      setOnboardingComplete(response.data.onboarding_complete);
    } catch {
      // If API is down, don't block the app -- assume complete
      setOnboardingComplete(true);
    }
  }, [user]);

  useEffect(() => {
    if (user) {
      checkOnboarding();
    }
  }, [user, checkOnboarding]);

  const handleAuthChange = (userData) => {
    setUser(userData);
    setOnboardingComplete(null); // Re-check onboarding after login
    navigate('/');
  };

  // Session timeout warning (SCRUM-214)
  const [showTimeoutWarning, setShowTimeoutWarning] = useState(false);
  const lastActivityRef = useRef(Date.now());
  const idleTimeoutMinutes = 60; // matches backend SESSION_IDLE_TIMEOUT_MINUTES
  const warningBeforeMinutes = 5; // show warning 5 min before timeout

  useEffect(() => {
    if (!user) return;

    const resetActivity = () => {
      lastActivityRef.current = Date.now();
      setShowTimeoutWarning(false);
    };

    const events = ['mousedown', 'keydown', 'scroll', 'touchstart'];
    events.forEach(e => window.addEventListener(e, resetActivity));

    const interval = setInterval(() => {
      const idleMs = Date.now() - lastActivityRef.current;
      const idleMin = idleMs / 60000;
      if (idleMin >= idleTimeoutMinutes - warningBeforeMinutes && idleMin < idleTimeoutMinutes) {
        setShowTimeoutWarning(true);
      } else if (idleMin >= idleTimeoutMinutes) {
        handleLogout();
      }
    }, 30000); // check every 30 seconds

    return () => {
      events.forEach(e => window.removeEventListener(e, resetActivity));
      clearInterval(interval);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const handleLogout = async () => {
    try {
      await axios.post('/api/auth/logout');
    } catch {
      // Ignore errors
    }
    setUser(false);
    setOnboardingComplete(null);
    setShowTimeoutWarning(false);
    navigate('/login');
  };

  // Still checking auth
  if (user === null) {
    return <PageLoader />;
  }

  // Public routes — always accessible (no auth required)
  // Normalize trailing slash so /demo/ matches the same as /demo
  const normalizedPath = location.pathname.replace(/\/$/, '') || '/';
  const isPublicRoute = ['/verify-email', '/reset-password', '/demo'].includes(normalizedPath)
    || normalizedPath.startsWith('/shared/');
  if (isPublicRoute) {
    return (
      <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="/verify-email" element={<VerifyEmail onVerified={(userData) => { setUser(userData); setOnboardingComplete(null); }} />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/shared/:token" element={<SharedReport />} />
          <Route path="/demo" element={<Demo />} />
        </Routes>
      </Suspense>
    );
  }

  // Not authenticated and not on login page -> redirect to login
  const isLoginRoute = location.pathname === '/login';
  if (user === false && !isLoginRoute) {
    return <Navigate to="/login" replace />;
  }

  // Authenticated but on login page -> redirect to home
  if (user && isLoginRoute) {
    return <Navigate to="/" replace />;
  }

  // Show login page (unauthenticated)
  if (!user) {
    return (
      <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="/login" element={<Login onAuthChange={handleAuthChange} />} />
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      </Suspense>
    );
  }

  // Authenticated but email not verified — show pending screen
  if (user && user.email_verified === false) {
    return (
      <Suspense fallback={<PageLoader />}>
        <EmailVerificationPending user={user} onLogout={handleLogout} onRefreshUser={(userData) => { setUser(userData); setOnboardingComplete(null); }} />
      </Suspense>
    );
  }

  // Authenticated — check onboarding
  if (onboardingComplete === null) {
    return <PageLoader />;
  }

  // If onboarding is not complete and user is NOT on /setup, redirect
  const isSetupRoute = location.pathname.startsWith('/setup');
  if (!onboardingComplete && !isSetupRoute) {
    return <Navigate to="/setup" replace />;
  }

  return (
    <div className="flex h-screen bg-[#0D1B2A] overflow-hidden">
      {/* Sidebar — hidden during onboarding; overlay drawer on mobile (CVA-83) */}
      {!isSetupRoute && (
        <Sidebar user={user} open={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      )}

      {/* Right panel */}
      <div className="flex flex-col flex-1 min-w-0 overflow-hidden">

        {/* Session timeout warning banner */}
        {showTimeoutWarning && (
          <div className="bg-yellow-50 border-b border-yellow-200 px-4 py-2 text-center text-sm text-yellow-800 shrink-0">
            Your session will expire soon due to inactivity.{' '}
            <button
              onClick={() => {
                lastActivityRef.current = Date.now();
                setShowTimeoutWarning(false);
                axios.get('/api/auth/me').catch(() => {});
              }}
              className="font-semibold underline hover:text-yellow-900"
            >
              Stay signed in
            </button>
          </div>
        )}

        {/* SCRUM-448: Jira reconnect banner */}
        {jiraReauthRequired && !isSetupRoute && (
          <ReconnectBanner onDismiss={() => setJiraReauthRequired(false)} />
        )}

        {/* Slim top bar — hidden during onboarding */}
        {!isSetupRoute && (
          <header className="bg-[#0D1B2A] border-b border-[#243B55] px-6 py-3 flex items-center justify-between shrink-0">
            {/* Mobile hamburger — opens the sidebar drawer (CVA-83) */}
            <button
              type="button"
              onClick={() => setSidebarOpen(true)}
              aria-label="Open menu"
              className="md:hidden text-[#E5C07B] hover:text-white p-1 -ml-1"
            >
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/>
              </svg>
            </button>
            <div className="flex items-center space-x-3">
              <Link
                to="/profile"
                className="text-sm text-[#E5C07B] hover:text-[#00C2A8] transition-colors no-underline"
                title="My Profile"
              >
                {user.full_name || user.email}
              </Link>
              {user.role !== 'viewer' && (
                <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-[#00C2A8]/15 text-[#00C2A8]">
                  {user.role === 'superadmin' ? 'Super Admin' : user.role === 'admin' ? 'Admin' : 'Owner'}
                </span>
              )}
              <button
                onClick={handleLogout}
                className="text-sm text-[#E5C07B] hover:text-[#EF4444] transition-colors"
              >
                Logout
              </button>
            </div>
          </header>
        )}

        {/* Page content */}
        <main className="flex-1 overflow-y-auto">
          <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route path="/setup" element={<OnboardingWizard onOnboardingComplete={() => setOnboardingComplete(true)} />} />
              <Route path="/" element={<Dashboard user={user} />} />
              <Route path="/upgrade" element={<Upgrade />} />
              <Route path="/reports" element={<Reports user={user} />} />
              <Route path="/about" element={<About />} />
              <Route path="/profile" element={<Profile user={user} onUserUpdate={setUser} />} />
              <Route path="/projects/:projectKey" element={<ProjectDetail user={user} />} />
              <Route path="/projects/:projectKey/roadmap" element={<Roadmap />} />
              <Route path="/portfolio" element={<PortfolioDashboard user={user} />} />
              {user.role !== 'viewer' && (
                <Route path="/backlog" element={<BacklogCreator />} />
              )}
              {(user.role === 'admin' || user.role === 'superadmin') &&
               (user.role === 'superadmin' || !(user.plan === 'free' || (user.plan === 'trial' && user.trial_days_remaining <= 0))) && (
                <>
                  <Route path="/config" element={<Configuration />} />
                  <Route path="/admin/users" element={<UserManagement />} />
                  <Route path="/admin/feedback" element={<FeedbackAdmin />} />
                  <Route path="/admin/audit-log" element={<AuditLog />} />
                  <Route path="/admin/analytics" element={<AdminAnalytics />} />
                  <Route path="/admin/insights/compare" element={<InsightCompare />} />
                  <Route path="/admin/rubrics" element={<RubricAdmin />} />
                </>
              )}
              {user.role === 'superadmin' && (
                <Route path="/admin/tenants" element={<TenantManagement />} />
              )}
              {user.role === 'superadmin' && (
                <Route path="/admin/quality" element={<QualityDashboard />} />
              )}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Suspense>
        </main>

      </div>

      {/* SCRUM-216: Guided tour — shown once after setup, hidden for viewers who skip onboarding */}
      {!isSetupRoute && onboardingComplete && user && !user.tour_seen && !tourDismissed && (
        <GuidedTour
          user={user}
          onDismiss={() => {
            setTourDismissed(true);
            // Reflect tour_seen in local user state so it won't show again if user
            // navigates without a full page reload.
            setUser(prev => prev ? { ...prev, tour_seen: true } : prev);
          }}
        />
      )}

      {/* Global floating feedback button - hidden during onboarding */}
      {!isSetupRoute && (
        <>
          <button
            onClick={() => setFeedbackOpen(true)}
            className="fixed bottom-6 right-6 bg-indigo-600 text-white px-5 py-3 rounded-full shadow-lg hover:bg-indigo-700 transition-colors flex items-center space-x-2 z-40"
            aria-label="Send feedback"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
            </svg>
            <span>Feedback</span>
          </button>
          <FeedbackModal
            isOpen={feedbackOpen}
            onClose={() => setFeedbackOpen(false)}
          />
        </>
      )}
    </div>
  );
}

function App() {
  return (
    <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <AppContent />
    </Router>
  );
}

export default App;
